import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Box, Typography, Button, Tabs, Tab, Grid, Card, CardContent, CardActions,
    IconButton, Chip, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Select, MenuItem, TablePagination, Dialog, DialogTitle, DialogContent,
    DialogContentText, DialogActions, LinearProgress, useTheme, Skeleton
} from '@mui/material';
import { get, post, patch, remove } from '../../services/api-services';
import TaskDialog from '../../dialogs/TaskDialog';
import AssignTaskDialog from '../../dialogs/AssignTaskDialog';
import EditAssignmentDialog from '../../dialogs/EditAssignmentDialog';
import ResponseDetailsDrawer from '../../dialogs/ResponseDetailsDrawer';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import ReplayIcon from '@mui/icons-material/Replay';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ForumIcon from '@mui/icons-material/Forum';
import DateRangePicker from '../../components/DateRangePicker';
import { useSearchParams } from 'react-router-dom';

const taskCache = {
    dailyTasks: null,
    assignedTasks: {},
    responses: {}
};

const responseStatusColors = {
    completed: { bg: 'rgba(76,175,80,0.15)', color: '#66bb6a' },
    missed: { bg: 'rgba(244,67,54,0.15)', color: '#ef5350' },
    pending: { bg: 'rgba(255,152,0,0.15)', color: '#ffa726' },
    partial: { bg: 'rgba(33,150,243,0.15)', color: '#64b5f6' },
};

const TaskSkaleton = () => {
    return (
        <CardContent>
            <Box className="flex items-center space-x-2">
                <Box className="flex-1 space-y-2">
                    <Skeleton variant="text" width="40%" height={20} />
                    <Skeleton variant="text" width="80%" height={20} />
                </Box>
                <Skeleton variant="circular" width={40} height={40} />
            </Box>
        </CardContent>
    )
}

export default function Task() {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';

    const cellSx = {
        color: isDarkMode ? '#ccc' : '#333',
        borderBottom: isDarkMode ? '1px solid #222' : '1px solid #e0e0e0',
        py: 1.5,
        fontSize: '0.8rem'
    };

    const headCellSx = {
        color: isDarkMode ? '#888' : '#555',
        borderBottom: isDarkMode ? '1px solid #333' : '1px solid #e0e0e0',
        py: 1.5,
        fontSize: '0.7rem',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        fontWeight: 'bold',
        backgroundColor: isDarkMode ? '#1a1a1a' : '#f5f5f5'
    };

    const [searchParams, setSearchParams] = useSearchParams();
    const tabValue = parseInt(searchParams.get('tab') || '0', 10);
    const [loading, setLoading] = useState(false);
    const [filterLoading, setFilterLoading] = useState(false);

    // Debounce timer refs
    const assignedDebounceRef = useRef(null);
    const responseDebounceRef = useRef(null);

    const [openDialog, setOpenDialog] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);

    // API Data state
    const [dailyTasks, setDailyTasks] = useState([]);
    const [taskLogs, setTaskLogs] = useState([]);
    const [responses, setResponses] = useState([]);

    // Assign Dialog state
    const [openAssignDialog, setOpenAssignDialog] = useState(false);
    const [taskToAssign, setTaskToAssign] = useState(null);
    const [taskDetails, setTaskDetails] = useState({ title: '', taskType: '', targetValue: '', calories: 0, dayNumber: 1 });

    // Assigned Tasks tab state
    const [filterTaskName, setFilterTaskName] = useState('');
    const [filterUser, setFilterUser] = useState('');
    const [filterType, setFilterType] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [filterDateFrom, setFilterDateFrom] = useState(null);
    const [filterDateTo, setFilterDateTo] = useState(null);
    const [assignedRowCount, setAssignedRowCount] = useState(0);
    const [responseRowCount, setResponseRowCount] = useState(0);

    // Edit Assignment drawer
    const [openEditAssignment, setOpenEditAssignment] = useState(false);
    const [editAssignment, setEditAssignment] = useState(null);

    // User Responses tab
    const [openResponseDrawer, setOpenResponseDrawer] = useState(false);
    const [selectedResponse, setSelectedResponse] = useState(null);
    const [responsePage, setResponsePage] = useState(0);
    const [responseFilterUser, setResponseFilterUser] = useState('');
    const [responseFilterTask, setResponseFilterTask] = useState('');
    const [responseFilterType, setResponseFilterType] = useState('');
    const [responseFilterStatus, setResponseFilterStatus] = useState('');
    const [responseDateFrom, setResponseDateFrom] = useState(null);
    const [responseDateTo, setResponseDateTo] = useState(null);

    // Delete Confirmation
    const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
    const [deleteItemId, setDeleteItemId] = useState(null);
    const [deleteItemType, setDeleteItemType] = useState('assignment'); // 'task' or 'assignment'

    const [editingRowId, setEditingRowId] = useState(null);
    const [editValue, setEditValue] = useState('');

    const formatDateParam = (d) => {
        if (!d) return null;
        if (typeof d === 'string') return d;
        try { return d.toISOString().split('T')[0]; } catch { return null; }
    };

    // Fetch paginated assigned tasks (server-side)
    const fetchAssignedTasks = useCallback(async (isFilterChange) => {
        const cacheKey = `${page}-${rowsPerPage}-${filterTaskName}-${filterUser}-${filterType}-${filterStatus}-${formatDateParam(filterDateFrom)}-${formatDateParam(filterDateTo)}`;
        
        if (taskCache.assignedTasks[cacheKey]) {
            setTaskLogs(taskCache.assignedTasks[cacheKey].data);
            setAssignedRowCount(taskCache.assignedTasks[cacheKey].total);
            setLoading(false);
            setFilterLoading(false);
        } else {
            if (isFilterChange) setFilterLoading(true); else setLoading(true);
        }

        try {
            const token = localStorage.getItem('accessToken');
            const params = new URLSearchParams();
            params.append('page', page);
            params.append('pageSize', rowsPerPage);
            if (filterTaskName) params.append('taskName', filterTaskName);
            if (filterUser) params.append('userName', filterUser);
            if (filterType && filterType !== 'all') params.append('taskType', filterType);
            if (filterStatus && filterStatus !== 'all') params.append('status', filterStatus);
            const fd = formatDateParam(filterDateFrom);
            const td = formatDateParam(filterDateTo);
            if (fd) params.append('fromDate', fd);
            if (td) params.append('toDate', td);

            const url = `/TaskLogs/paginate?${params.toString()}&access_token=${token}`;
            const res = await get(url);
            if (res?.statusCode === 200) {
                const fetchedData = res.data.data || [];
                const fetchedTotal = res.data.total || 0;
                setTaskLogs(fetchedData);
                setAssignedRowCount(fetchedTotal);
                taskCache.assignedTasks[cacheKey] = { data: fetchedData, total: fetchedTotal };
            } else {
                console.error('fetchAssignedTasks failed', res);
                // Fallback: fetch all TaskLogs with includes and apply client-side filtering/pagination
                try {
                    const fallbackUrl = `/TaskLogs?filter=${encodeURIComponent(JSON.stringify({ include: ['customer', 'dailyTask'], order: 'date DESC' }))}&access_token=${token}`;
                    const allRes = await get(fallbackUrl);
                    if (allRes?.statusCode === 200) {
                        const all = allRes.data || [];
                        const filtered = all.filter(t => {
                            const taskDate = new Date(t.date);
                            const fromDate = filterDateFrom ? new Date(filterDateFrom) : null;
                            const toDate = filterDateTo ? new Date(filterDateTo) : null;
                            taskDate.setHours(0, 0, 0, 0);
                            if (fromDate) fromDate.setHours(0, 0, 0, 0);
                            if (toDate) toDate.setHours(0, 0, 0, 0);
                            return (!filterTaskName || t.dailyTask?.title?.toLowerCase().includes(filterTaskName.toLowerCase())) &&
                                (!filterUser || t.customer?.name?.toLowerCase().includes(filterUser.toLowerCase())) &&
                                (!filterType || filterType === 'all' || t.dailyTask?.taskType === filterType.toLowerCase()) &&
                                (!filterStatus || filterStatus === 'all' || t.status === filterStatus.toLowerCase()) &&
                                (!fromDate || taskDate >= fromDate) &&
                                (!toDate || taskDate <= toDate);
                        });
                        const total = filtered.length;
                        const start = page * rowsPerPage;
                        const pageData = filtered.slice(start, start + rowsPerPage);
                        setTaskLogs(pageData || []);
                        setAssignedRowCount(total || 0);
                    }
                } catch (fallbackErr) {
                    console.error('fetchAssignedTasks fallback error', fallbackErr);
                }
            }
        } catch (e) {
            console.error('Error fetching assigned tasks:', e);
        }
        setLoading(false);
        setFilterLoading(false);
    }, [page, rowsPerPage, filterTaskName, filterUser, filterType, filterStatus, filterDateFrom, filterDateTo]);

    // Fetch paginated user responses (server-side)
    const fetchResponses = useCallback(async (isFilterChange) => {
        const cacheKey = `${responsePage}-${10}-${responseFilterUser}-${responseFilterTask}-${responseFilterType}-${responseFilterStatus}-${formatDateParam(responseDateFrom)}-${formatDateParam(responseDateTo)}`;
        
        if (taskCache.responses[cacheKey]) {
            setResponses(taskCache.responses[cacheKey].data);
            setResponseRowCount(taskCache.responses[cacheKey].total);
            setLoading(false);
            setFilterLoading(false);
        } else {
            if (isFilterChange) setFilterLoading(true); else setLoading(true);
        }

        try {
            const token = localStorage.getItem('accessToken');
            const params = new URLSearchParams();
            params.append('page', responsePage);
            params.append('pageSize', 10);
            if (responseFilterUser) params.append('userName', responseFilterUser);
            if (responseFilterTask) params.append('taskName', responseFilterTask);
            if (responseFilterType && responseFilterType !== 'all') params.append('taskType', responseFilterType);
            if (responseFilterStatus && responseFilterStatus !== 'all') params.append('status', responseFilterStatus);
            const fd = formatDateParam(responseDateFrom);
            const td = formatDateParam(responseDateTo);
            if (fd) params.append('fromDate', fd);
            if (td) params.append('toDate', td);

            const url = `/TaskLogs/paginate?${params.toString()}&access_token=${token}`;
            const res = await get(url);
            if (res?.statusCode === 200) {
                const fetchedData = res.data.data || [];
                const fetchedTotal = res.data.total || 0;
                setResponses(fetchedData);
                setResponseRowCount(fetchedTotal);
                taskCache.responses[cacheKey] = { data: fetchedData, total: fetchedTotal };
            } else {
                console.error('fetchResponses failed', res);
                // Fallback: fetch all TaskLogs and apply response filters client-side
                try {
                    const fallbackUrl = `/TaskLogs?filter=${encodeURIComponent(JSON.stringify({ include: ['customer', 'dailyTask'], order: 'date DESC' }))}&access_token=${token}`;
                    const allRes = await get(fallbackUrl);
                    if (allRes?.statusCode === 200) {
                        const all = allRes.data || [];
                        const filtered = all.filter(r => {
                            const baseDate = r.createdAt || r.date;
                            const responseDate = baseDate ? new Date(baseDate) : null;
                            const fromDate = responseDateFrom ? new Date(responseDateFrom) : null;
                            const toDate = responseDateTo ? new Date(responseDateTo) : null;
                            if (responseDate) responseDate.setHours(0, 0, 0, 0);
                            if (fromDate) fromDate.setHours(0, 0, 0, 0);
                            if (toDate) toDate.setHours(0, 0, 0, 0);
                            return (!responseFilterUser || r.customer?.name?.toLowerCase().includes(responseFilterUser.toLowerCase())) &&
                                (!responseFilterTask || r.dailyTask?.title?.toLowerCase().includes(responseFilterTask.toLowerCase())) &&
                                (!responseFilterType || responseFilterType === 'all' || r.dailyTask?.taskType === responseFilterType.toLowerCase()) &&
                                (!responseFilterStatus || responseFilterStatus === 'all' || r.status === responseFilterStatus.toLowerCase()) &&
                                (!fromDate || (responseDate && responseDate >= fromDate)) &&
                                (!toDate || (responseDate && responseDate <= toDate));
                        });
                        const total = filtered.length;
                        const start = responsePage * 10;
                        const pageData = filtered.slice(start, start + 10);
                        setResponses(pageData || []);
                        setResponseRowCount(total || 0);
                    }
                } catch (fallbackErr) {
                    console.error('fetchResponses fallback error', fallbackErr);
                }
            }
        } catch (e) {
            console.error('Error fetching responses:', e);
        }
        setLoading(false);
        setFilterLoading(false);
    }, [responsePage, responseFilterUser, responseFilterTask, responseFilterType, responseFilterStatus, responseDateFrom, responseDateTo]);


    const handleTargetEdit = (row) => {
        setEditingRowId(row.id);
        setEditValue(row.value || row.dailyTask?.targetValue || '');
    };

    const handleTargetSave = async (row) => {
        if (!editingRowId) return;
        setLoading(true);
        try {
            await patch(`/TaskLogs/${row.id}`, { value: editValue });
            fetchData();
        } catch (error) {
            console.error("Error saving target:", error);
        }
        setEditingRowId(null);
        setLoading(false);
    };

    const fetchData = async () => {
        if (taskCache.dailyTasks) {
            setDailyTasks(taskCache.dailyTasks);
            setLoading(false);
        } else {
            setLoading(true);
        }

        try {
            const tasksRes = await get('/DailyTasks');
            if (tasksRes?.data) {   
                setDailyTasks(tasksRes.data);
                taskCache.dailyTasks = tasksRes.data;
            }
        } catch (error) {
            console.error("Error fetching tasks:", error);
        }
        setLoading(false);

        // Refresh all tab data concurrently
        fetchAssignedTasks();
        fetchResponses();
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Re-fetch assigned tasks when pagination or filters change and Assigned tab is active
    useEffect(() => {
        if (assignedDebounceRef.current) clearTimeout(assignedDebounceRef.current);
        assignedDebounceRef.current = setTimeout(() => {
            fetchAssignedTasks(true);
        }, 400);
        return () => clearTimeout(assignedDebounceRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterTaskName, filterUser]);

    useEffect(() => {
        fetchAssignedTasks();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, rowsPerPage, filterType, filterStatus, filterDateFrom, filterDateTo]);

    // Re-fetch responses — debounce text filters, instant for dropdowns/pagination
    useEffect(() => {
        if (responseDebounceRef.current) clearTimeout(responseDebounceRef.current);
        responseDebounceRef.current = setTimeout(() => {
            fetchResponses(true);
        }, 400);
        return () => clearTimeout(responseDebounceRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [responseFilterUser, responseFilterTask]);

    useEffect(() => {
        fetchResponses();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [responsePage, responseFilterType, responseFilterStatus, responseDateFrom, responseDateTo]);

    const handleTabChange = (event, newValue) => {
        const newParams = new URLSearchParams(searchParams);
        if (newValue === 0) newParams.delete('tab');
        else newParams.set('tab', String(newValue));
        setSearchParams(newParams, { replace: true });
    };

    const handleOpenDialog = (task = null) => {
        if (task) {
            setIsEditMode(true);
            setTaskDetails({
                ...task,
                taskType: task.taskType || 'workout',
                targetValue: task.targetValue || ''
            });
        } else {
            setIsEditMode(false);
            setTaskDetails({ title: '', taskType: 'workout', targetValue: '', calories: 0, dayNumber: 1 });
        }
        setOpenDialog(true);
    };

    const handleOpenAssignDialog = (task) => {
        setTaskToAssign(task);
        setOpenAssignDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    const handleSaveTask = async () => {
        setLoading(true);
        try {
            const payload = {
                title: taskDetails.title,
                taskType: taskDetails.taskType.toLowerCase(),
                targetValue: taskDetails.targetValue,
                calories: taskDetails.calories || 0,
                dayNumber: taskDetails.dayNumber || 1
            };

            if (isEditMode) {
                await patch(`/DailyTasks/${taskDetails.id}`, payload);
            } else {
                await post('/DailyTasks', payload);
            }
            fetchData();
            setOpenDialog(false);
        } catch (error) {
            console.error("Error saving task:", error);
        }
        setLoading(false);
    };

    const handleEditAssignment = (row) => {
        setEditAssignment({ ...row });
        setOpenEditAssignment(true);
    };

    const handleSaveEditAssignment = async () => {
        setLoading(true);
        try {
            await patch(`/TaskLogs/${editAssignment.id}`, {
                value: editAssignment.value,
                date: editAssignment.date,
                status: editAssignment.status
            });
            fetchData();
            setOpenEditAssignment(false);
        } catch (error) {
            console.error("Error updating assignment:", error);
        }
        setLoading(false);
    };

    const handleResetFilters = () => {
        setFilterTaskName('');
        setFilterUser('');
        setFilterType('');
        setFilterStatus('');
        setFilterDateFrom(null);
        setFilterDateTo(null);
    };

    const handleResetResponseFilters = () => {
        setResponseFilterUser('');
        setResponseFilterTask('');
        setResponseFilterType('');
        setResponseFilterStatus('');
        setResponseDateFrom(null);
        setResponseDateTo(null);
        setResponsePage(0);
    };

    const handleDeleteClick = (id, type = 'assignment') => {
        setDeleteItemId(id);
        setDeleteItemType(type);
        setOpenDeleteConfirm(true);
    };

    const handleConfirmDelete = async () => {
        try {
            const endpoint = deleteItemType === 'task' ? `/DailyTasks/${deleteItemId}` : `/TaskLogs/${deleteItemId}`;
            await remove(endpoint);
            setOpenDeleteConfirm(false);
            fetchData();
        } catch (error) {
            console.error("Error deleting item:", error);
        }
    };

    // Server-side filtered/paginated results (populated by fetchAssignedTasks / fetchResponses)
    const filteredAssigned = taskLogs;
    const filteredResponses = responses;

    const formatDate = (d) => {
        if (!d) return '';
        const dt = new Date(d);
        if (Number.isNaN(dt.getTime())) return '';
        return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    const selectSx = {
        backgroundColor: isDarkMode ? '#111' : '#fff',
        border: isDarkMode ? '1px solid #333' : '1px solid #ccc',
        borderRadius: '6px',
        color: isDarkMode ? '#fff' : '#000',
        fontSize: '0.8rem',
        height: '38px',
        '.MuiOutlinedInput-notchedOutline': { border: 0 },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 0 },
        '.MuiSelect-icon': { color: isDarkMode ? '#666' : '#999' }
    };

    const primaryButtonSx = {
        backgroundColor: '#3F51B5',
        color: '#fff',
        textTransform: 'none',
        boxShadow: '0 2px 10px rgba(63, 81, 181, 0.25)',
        transition: 'background-color 0.2s ease, box-shadow 0.2s ease, transform 0.08s ease',
        '&:hover': {
            backgroundColor: '#4F5BCD',
            boxShadow: '0 6px 18px rgba(63, 81, 181, 0.35)'
        },
        '&:active': {
            transform: 'translateY(1px)'
        }
    };

    return (
        <Box sx={{ color: isDarkMode ? '#fff' : '#000' }}>
            
            <Box sx={{
                backgroundColor: isDarkMode ? '#1a1a1a' : '#fff',
                px: 3, pt: 3, pb: 3, mb: 3,
                borderBottom: isDarkMode ? '1px solid #222' : '1px solid #e0e0e0'
            }}>
                <Box display="flex" flexDirection="column" mb={3}>
                    <Typography variant="h5" fontWeight="bold" sx={{ letterSpacing: '0.5px', color: isDarkMode ? '#fff' : '#232323' }}>WeightWonder</Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>Task Library</Typography>
                </Box>

                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Tabs value={tabValue} onChange={handleTabChange}
                            sx={{
                                minHeight: '40px',
                                backgroundColor: isDarkMode ? '#1a1a1a !important' : '#fff !important',
                            }}>
                            <Tab
                                icon={<CheckCircleOutlineIcon fontSize="small" />}
                                iconPosition="start"
                                label="Tasks"
                                sx={{ minHeight: '40px', textTransform: 'none', px: 3 }}
                            />
                            <Tab
                                icon={<AssignmentIcon fontSize="small" />}
                                iconPosition="start"
                                label="Assigned Tasks"
                                sx={{ minHeight: '40px', textTransform: 'none', px: 3 }}
                            />
                            <Tab
                                icon={<ForumIcon fontSize="small" />}
                                iconPosition="start"
                                label="User Responses"
                                sx={{ minHeight: '40px', textTransform: 'none', px: 3 }}
                            />
                        </Tabs>
                    </Box>
                    {tabValue === 0 && (
                        <Button
                            variant="contained"
                            onClick={() => handleOpenDialog()}
                            startIcon={<AddIcon />}
                            sx={{
                                ...primaryButtonSx,
                                borderRadius: '6px',
                                px: 3,
                                height: 40
                            }}
                        >
                            Create Task
                        </Button>
                    )}
                </Box>
            </Box>
            <Box sx={{ position: 'relative', height: '2px' }}>
                {loading && !filterLoading && <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: '2px', backgroundColor: 'transparent', '& .MuiLinearProgress-bar': { backgroundColor: '#3c50b4' } }} />}
            </Box>

            <Box sx={{ px: 3, pb: 3 }}>
                {/* ═══ TAB 0: Tasks ═══ */}
                {tabValue === 0 && (<>
                    <Typography variant="body2" color="textSecondary" mb={3} sx={{ letterSpacing: '0.5px' }}>
                        Pre Defined tasks
                    </Typography>

                    <Typography variant="body2" fontWeight="bold" mb={2} sx={{ textTransform: 'uppercase', letterSpacing: 1, color: isDarkMode ? '#e0e0e0' : '#424242' }}>
                        WORKOUT
                    </Typography>

                    <Grid container spacing={3} mb={4}>
                        {dailyTasks.filter(t => t.taskType === 'workout').map((task) => (
                            <Grid item xs={12} sm={6} lg={4} key={task.id}>
                                <Card sx={{
                                    backgroundColor: isDarkMode ? '#1e1e1e' : '#fff',
                                    color: isDarkMode ? '#fff' : '#000',
                                    borderRadius: 2,
                                    border: isDarkMode ? '1px solid #333' : '1px solid #e0e0e0',
                                    boxShadow: isDarkMode ? 'none' : '0 2px 8px rgba(0,0,0,0.05)'
                                }}>
                                    <CardContent>
                                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                                            <Typography variant="subtitle1" fontWeight="bold" sx={{ color: isDarkMode ? '#fff' : '#232323' }}>{task.title}</Typography>
                                            <Chip label={task.taskType} size="small" sx={{
                                                backgroundColor: isDarkMode ? 'rgba(156, 39, 176, 0.1)' : 'rgba(156, 39, 176, 0.08)',
                                                color: isDarkMode ? '#ce93d8' : '#7b1fa2',
                                                fontSize: '0.65rem', borderRadius: '4px'
                                            }} />
                                        </Box>
                                        <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.8rem' }}>
                                            Target: {task.targetValue} mins
                                        </Typography>
                                    </CardContent>
                                    <CardActions sx={{ px: 2, pb: 2 }}>
                                        <Button variant="contained" fullWidth onClick={() => handleOpenAssignDialog(task)} sx={primaryButtonSx}>
                                            Assign
                                        </Button>
                                        <IconButton onClick={() => handleOpenDialog(task)} sx={{
                                            border: isDarkMode ? '1px solid #333' : '1px solid #e0e0e0',
                                            borderRadius: 1, ml: 1,
                                            color: isDarkMode ? '#999' : '#666',
                                            '&:hover': { color: isDarkMode ? '#fff' : '#000', backgroundColor: isDarkMode ? 'transparent' : 'rgba(0,0,0,0.04)' }
                                        }}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton onClick={() => handleDeleteClick(task.id, 'task')} sx={{
                                            border: isDarkMode ? '1px solid #333' : '1px solid #e0e0e0',
                                            borderRadius: 1, ml: 1,
                                            color: isDarkMode ? '#999' : '#666',
                                            '&:hover': { color: '#e57373', backgroundColor: isDarkMode ? 'transparent' : 'rgba(0,0,0,0.04)' }
                                        }}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </CardActions>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>

                    <Typography variant="body2" fontWeight="bold" mb={2} sx={{ textTransform: 'uppercase', letterSpacing: 1, color: isDarkMode ? '#e0e0e0' : '#424242' }}>
                        FOOD / DIET
                    </Typography>

                    <Grid container spacing={3}>
                        {dailyTasks.filter(t => ['food', 'breakfast', 'lunch', 'dinner', 'diet'].includes(t.taskType)).map((task) => (
                            <Grid item xs={12} sm={6} lg={4} key={task.id}>
                                <Card sx={{
                                    backgroundColor: isDarkMode ? '#1e1e1e' : '#fff',
                                    color: isDarkMode ? '#fff' : '#000',
                                    borderRadius: 2,
                                    border: isDarkMode ? '1px solid #333' : '1px solid #e0e0e0',
                                    boxShadow: isDarkMode ? 'none' : '0 2px 8px rgba(0,0,0,0.05)'
                                }}>
                                    <CardContent>
                                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                                            <Typography variant="subtitle1" fontWeight="bold" sx={{ color: isDarkMode ? '#fff' : '#232323' }}>{task.title}</Typography>
                                            <Chip label={task.taskType} size="small" sx={{
                                                backgroundColor: isDarkMode ? 'rgba(255, 152, 0, 0.1)' : 'rgba(239, 108, 0, 0.08)',
                                                color: isDarkMode ? '#ffb74d' : '#e65100',
                                                fontSize: '0.65rem', borderRadius: '4px'
                                            }} />
                                        </Box>
                                        <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.8rem' }}>
                                            Calories: {task.calories} • Target: {task.targetValue}
                                        </Typography>
                                    </CardContent>
                                    <CardActions sx={{ px: 2, pb: 2 }}>
                                        <Button variant="contained" fullWidth onClick={() => handleOpenAssignDialog(task)} sx={primaryButtonSx}>
                                            Assign
                                        </Button>
                                        <IconButton onClick={() => handleOpenDialog(task)} sx={{
                                            border: isDarkMode ? '1px solid #333' : '1px solid #e0e0e0',
                                            borderRadius: 1, ml: 1,
                                            color: isDarkMode ? '#999' : '#666',
                                            '&:hover': { color: isDarkMode ? '#fff' : '#000', backgroundColor: isDarkMode ? 'transparent' : 'rgba(0,0,0,0.04)' }
                                        }}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton onClick={() => handleDeleteClick(task.id, 'task')} sx={{
                                            border: isDarkMode ? '1px solid #333' : '1px solid #e0e0e0',
                                            borderRadius: 1, ml: 1,
                                            color: isDarkMode ? '#999' : '#666',
                                            '&:hover': { color: '#e57373', backgroundColor: isDarkMode ? 'transparent' : 'rgba(0,0,0,0.04)' }
                                        }}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </CardActions>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>

                    <Typography variant="body2" fontWeight="bold" mb={2} mt={4} sx={{ textTransform: 'uppercase', letterSpacing: 1, color: isDarkMode ? '#e0e0e0' : '#424242' }}>
                        BEVERAGE
                    </Typography>

                    <Grid container spacing={3}>
                        {dailyTasks.filter(t => t.taskType === 'beverage').map((task) => (
                            <Grid item xs={12} sm={6} lg={4} key={task.id}>
                                <Card sx={{
                                    backgroundColor: isDarkMode ? '#1e1e1e' : '#fff',
                                    color: isDarkMode ? '#fff' : '#000',
                                    borderRadius: 2,
                                    border: isDarkMode ? '1px solid #333' : '1px solid #e0e0e0',
                                    boxShadow: isDarkMode ? 'none' : '0 2px 8px rgba(0,0,0,0.05)'
                                }}>
                                    <CardContent>
                                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                                            <Typography variant="subtitle1" fontWeight="bold" sx={{ color: isDarkMode ? '#fff' : '#232323' }}>{task.title}</Typography>
                                            <Chip label={task.taskType} size="small" sx={{
                                                backgroundColor: isDarkMode ? 'rgba(0, 150, 136, 0.1)' : 'rgba(0, 150, 136, 0.08)',
                                                color: isDarkMode ? '#80cbc4' : '#00796b',
                                                fontSize: '0.65rem', borderRadius: '4px'
                                            }} />
                                        </Box>
                                        <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.8rem' }}>
                                            Target: {task.targetValue}
                                        </Typography>
                                    </CardContent>
                                    <CardActions sx={{ px: 2, pb: 2 }}>
                                        <Button variant="contained" fullWidth onClick={() => handleOpenAssignDialog(task)} sx={primaryButtonSx}>
                                            Assign
                                        </Button>
                                        <IconButton onClick={() => handleOpenDialog(task)} sx={{
                                            border: isDarkMode ? '1px solid #333' : '1px solid #e0e0e0',
                                            borderRadius: 1, ml: 1,
                                            color: isDarkMode ? '#999' : '#666',
                                            '&:hover': { color: isDarkMode ? '#fff' : '#000', backgroundColor: isDarkMode ? 'transparent' : 'rgba(0,0,0,0.04)' }
                                        }}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton onClick={() => handleDeleteClick(task.id, 'task')} sx={{
                                            border: isDarkMode ? '1px solid #333' : '1px solid #e0e0e0',
                                            borderRadius: 1, ml: 1,
                                            color: isDarkMode ? '#999' : '#666',
                                            '&:hover': { color: '#e57373', backgroundColor: isDarkMode ? 'transparent' : 'rgba(0,0,0,0.04)' }
                                        }}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </CardActions>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </>)}

                {/* ═══ TAB 1: Assigned Tasks ═══ */}
                {tabValue === 1 && (
                    <Box>
                        {/* Filters */}
                        <Box sx={{
                            backgroundColor: isDarkMode ? '#1a1a1a' : '#fff',
                            border: isDarkMode ? '1px solid #222' : '1px solid #e0e0e0',
                            borderRadius: '8px', p: 3, mb: 3,
                            boxShadow: isDarkMode ? '0 6px 20px rgba(0,0,0,0.25)' : '0 4px 12px rgba(0,0,0,0.05)'
                        }}>
                            <Box display="flex" alignItems="center" gap={1} mb={2}>
                                <FilterListIcon sx={{ color: isDarkMode ? '#888' : '#666', fontSize: 18 }} />
                                <Typography variant="body2" sx={{ color: isDarkMode ? '#aaa' : '#555' }}>Filters</Typography>
                            </Box>
                            <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
                                <input placeholder="Search User" value={filterUser} onChange={e => setFilterUser(e.target.value)}
                                    style={{
                                        flex: 1, minWidth: '120px', padding: '8px 12px',
                                        backgroundColor: isDarkMode ? '#111' : '#fff',
                                        border: isDarkMode ? '1px solid #333' : '1px solid #ccc',
                                        borderRadius: '6px',
                                        color: isDarkMode ? '#fff' : '#000',
                                        fontSize: '0.8rem', outline: 'none'
                                    }} />
                                <input placeholder="Search task name" value={filterTaskName} onChange={e => setFilterTaskName(e.target.value)}
                                    style={{
                                        flex: 1, minWidth: '140px', padding: '8px 12px',
                                        backgroundColor: isDarkMode ? '#111' : '#fff',
                                        border: isDarkMode ? '1px solid #333' : '1px solid #ccc',
                                        borderRadius: '6px',
                                        color: isDarkMode ? '#fff' : '#000',
                                        fontSize: '0.8rem', outline: 'none'
                                    }} />
                                <Select size="small" displayEmpty value={filterType} onChange={e => setFilterType(e.target.value)} sx={{ ...selectSx, minWidth: 150 }}
                                    renderValue={(selected) => {
                                        if (selected === "") {
                                            return <span style={{ color: isDarkMode ? '#888' : '#999' }}>Select Task Type</span>;
                                        }
                                        if (selected === "all") {
                                            return "All";
                                        }
                                        return selected.charAt(0).toUpperCase() + selected.slice(1);
                                    }}
                                    MenuProps={{ PaperProps: { sx: { bgcolor: isDarkMode ? '#1e1e1e' : '#fff', color: isDarkMode ? '#fff' : '#000', border: isDarkMode ? '1px solid #333' : '1px solid #ccc', '& .MuiMenuItem-root': { '&:hover': { bgcolor: isDarkMode ? '#333' : '#f5f5f5' }, '&.Mui-selected': { bgcolor: '#4f5b93 !important', color: '#fff' } } } } }}>
                                    <MenuItem value="all">All</MenuItem>
                                    <MenuItem value="workout">Workout</MenuItem>
                                    <MenuItem value="food">Food</MenuItem>
                                    <MenuItem value="beverage">Beverage</MenuItem>
                                </Select>
                                <Select size="small" displayEmpty value={filterStatus} onChange={e => setFilterStatus(e.target.value)} sx={{ ...selectSx, minWidth: 130 }}
                                    renderValue={(selected) => {
                                        if (selected === "") {
                                            return <span style={{ color: isDarkMode ? '#888' : '#999' }}>Select Status</span>;
                                        }
                                        if (selected === "all") {
                                            return "All";
                                        }
                                        return selected.charAt(0).toUpperCase() + selected.slice(1);
                                    }}
                                    MenuProps={{ PaperProps: { sx: { bgcolor: isDarkMode ? '#1e1e1e' : '#fff', color: isDarkMode ? '#fff' : '#000', border: isDarkMode ? '1px solid #333' : '1px solid #ccc', '& .MuiMenuItem-root': { '&:hover': { bgcolor: isDarkMode ? '#333' : '#f5f5f5' }, '&.Mui-selected': { bgcolor: '#4f5b93 !important', color: '#fff' } } } } }}>
                                    <MenuItem value="all">All</MenuItem>
                                    <MenuItem value="pending">Pending</MenuItem>
                                    <MenuItem value="completed">Completed</MenuItem>
                                    <MenuItem value="partial">Partial</MenuItem>
                                </Select>
                                <DateRangePicker
                                    fromDate={filterDateFrom}
                                    toDate={filterDateTo}
                                    onChange={(from, to) => {
                                        setFilterDateFrom(from);
                                        setFilterDateTo(to);
                                    }}
                                />
                                <Button startIcon={<ReplayIcon fontSize="small" />} onClick={handleResetFilters}
                                    sx={{
                                        color: isDarkMode ? '#888' : '#666',
                                        textTransform: 'none',
                                        border: isDarkMode ? '1px solid #333' : '1px solid #ccc',
                                        borderRadius: '6px', px: 2,
                                        '&:hover': { color: isDarkMode ? '#fff' : '#000', borderColor: isDarkMode ? '#555' : '#888', backgroundColor: isDarkMode ? 'transparent' : 'rgba(0,0,0,0.04)' }
                                    }}>
                                    Reset
                                </Button>
                            </Box>
                        </Box>

                        {/* Table */}
                        <Box sx={{
                            backgroundColor: isDarkMode ? '#1a1a1a' : '#fff',
                            border: isDarkMode ? '1px solid #222' : '1px solid #e0e0e0',
                            borderRadius: '8px', p: 1.5,
                            boxShadow: isDarkMode ? '0 6px 20px rgba(0,0,0,0.25)' : '0 4px 12px rgba(0,0,0,0.05)',
                            position: 'relative', minHeight: '200px'
                        }}>
                            {filterLoading && (
                                <Box sx={{
                                    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                                    backgroundColor: isDarkMode ? 'rgba(26,26,26,0.7)' : 'rgba(255,255,255,0.7)',
                                    zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px'
                                }}>
                                    <Box sx={{
                                        width: 32, height: 32,
                                        border: isDarkMode ? '3px solid #333' : '3px solid #e0e0e0',
                                        borderTop: '3px solid #3c50b4', borderRadius: '50%',
                                        animation: 'spin 0.8s linear infinite', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } }
                                    }} />
                                </Box>
                            )}
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={headCellSx}>Task Name</TableCell>
                                            <TableCell sx={headCellSx}>Assigned To</TableCell>
                                            <TableCell sx={headCellSx}>Task Type</TableCell>
                                            <TableCell sx={headCellSx}>Target Duration</TableCell>
                                            <TableCell sx={headCellSx}>Start Date</TableCell>
                                            <TableCell sx={headCellSx}>End Date</TableCell>
                                            <TableCell sx={headCellSx}>Status</TableCell>
                                            <TableCell sx={headCellSx} align="right">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredAssigned.map((row) => (
                                            <TableRow key={row.id} sx={{ '&:hover': { backgroundColor: 'rgba(255,255,255,0.02)' } }}>
                                                <TableCell sx={cellSx}>{row.dailyTask?.title}</TableCell>
                                                <TableCell sx={cellSx}>{row.customer?.name}</TableCell>
                                                <TableCell sx={cellSx}>
                                                    <Chip label={row.dailyTask?.taskType} size="small" sx={{
                                                        fontSize: '0.65rem', borderRadius: '4px',
                                                        backgroundColor: row.dailyTask?.taskType === 'workout' ? 'rgba(156,39,176,0.15)' : 'rgba(255,152,0,0.15)',
                                                        color: row.dailyTask?.taskType === 'workout' ? '#ce93d8' : '#ffb74d',
                                                    }} />
                                                </TableCell>
                                                <TableCell sx={cellSx}>
                                                    {editingRowId === row.id ? (
                                                        <Box sx={{
                                                            display: 'inline-flex', alignItems: 'center',
                                                            backgroundColor: isDarkMode ? '#0a0a0a' : '#f9f9f9',
                                                            border: '1px solid #3c50b4', borderRadius: '8px', px: 1.5, py: 0.8,
                                                            boxShadow: '0 0 10px rgba(92, 107, 192, 0.1)'
                                                        }}>
                                                            <input
                                                                autoFocus
                                                                type="text"
                                                                value={editValue}
                                                                onChange={(e) => setEditValue(e.target.value)}
                                                                onBlur={() => handleTargetSave(row)}
                                                                onKeyDown={(e) => e.key === 'Enter' && handleTargetSave(row)}
                                                                style={{
                                                                    width: '50px', background: 'transparent', border: 'none', outline: 'none',
                                                                    color: isDarkMode ? '#fff' : '#000', fontSize: '0.85rem'
                                                                }}
                                                            />
                                                            <Typography variant="caption" sx={{ color: isDarkMode ? '#aaa' : '#666', ml: 0.5, fontSize: '0.75rem' }}>
                                                                {row.dailyTask?.taskType === 'workout' ? 'min' :
                                                                    ['food', 'breakfast', 'lunch', 'dinner', 'diet'].includes(row.dailyTask?.taskType) ? 'gm' :
                                                                        ['water', 'beverage', 'beverages'].includes(row.dailyTask?.taskType) ? 'L' : ''}
                                                            </Typography>
                                                        </Box>
                                                    ) : (
                                                        <Box
                                                            onClick={() => handleTargetEdit(row)}
                                                            sx={{
                                                                display: 'inline-flex', alignItems: 'center', cursor: 'pointer',
                                                                '&:hover': {
                                                                    '& .MuiTypography-root': { color: isDarkMode ? '#fff' : '#000' },
                                                                    '& .MuiSvgIcon-root': { color: isDarkMode ? '#fff' : '#000', opacity: 1 }
                                                                }
                                                            }}
                                                        >
                                                            <Typography variant="body2" sx={{ fontSize: '0.85rem', color: isDarkMode ? '#aaa' : '#555', transition: 'color 0.2s' }}>
                                                                {row.value || row.dailyTask?.targetValue} {row.dailyTask?.taskType === 'workout' ? 'min' :
                                                                    ['food', 'breakfast', 'lunch', 'dinner', 'diet'].includes(row.dailyTask?.taskType) ? 'gm' :
                                                                        ['water', 'beverage', 'beverages'].includes(row.dailyTask?.taskType) ? 'L' : ''}
                                                            </Typography>
                                                            <EditIcon sx={{ fontSize: 14, color: isDarkMode ? '#666' : '#888', ml: 1, opacity: 0.5, transition: 'all 0.2s' }} />
                                                        </Box>
                                                    )}
                                                </TableCell>
                                                <TableCell sx={cellSx}>{formatDate(row.date)}</TableCell>
                                                <TableCell sx={cellSx}>{formatDate(row.endDate || row.date)}</TableCell>
                                                <TableCell sx={cellSx}>
                                                    <Chip label={row.status} size="small" sx={{ fontSize: '0.65rem', borderRadius: '4px', backgroundColor: responseStatusColors[row.status]?.bg, color: responseStatusColors[row.status]?.color }} />
                                                </TableCell>
                                                <TableCell sx={cellSx} align="right">
                                                    <IconButton size="small" onClick={() => handleEditAssignment(row)} sx={{ color: '#888', '&:hover': { color: '#fff' } }}>
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton size="small" onClick={() => handleDeleteClick(row.id, 'assignment')} sx={{ color: '#888', '&:hover': { color: '#e57373' }, ml: 1 }}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {!filterLoading && filteredAssigned.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={8} sx={{ textAlign: 'center', py: 6, borderBottom: 'none' }}>
                                                    <Typography variant="body2" sx={{ color: '#666', fontSize: '0.85rem' }}>No data found</Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                        <TablePagination
                            component="div"
                            count={assignedRowCount}
                            page={page}
                            onPageChange={(e, p) => setPage(p)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                            sx={{
                                color: isDarkMode ? '#888' : '#555',
                                '.MuiTablePagination-selectIcon': { color: isDarkMode ? '#888' : '#555' },
                                '.MuiTablePagination-select': { color: isDarkMode ? '#fff' : '#000' }
                            }}
                        />
                    </Box>
                )}

                {/* ═══ TAB 2: User Responses ═══ */}
                {tabValue === 2 && (
                    <Box>
                        <Box sx={{
                            backgroundColor: isDarkMode ? '#1a1a1a' : '#fff',
                            border: isDarkMode ? '1px solid #222' : '1px solid #e0e0e0',
                            borderRadius: '8px', p: 3, mb: 3,
                            boxShadow: isDarkMode ? '0 6px 20px rgba(0,0,0,0.25)' : '0 4px 12px rgba(0,0,0,0.05)'
                        }}>
                            <Box display="flex" alignItems="center" gap={1} mb={2}>
                                <FilterListIcon sx={{ color: isDarkMode ? '#888' : '#666', fontSize: 18 }} />
                                <Typography variant="body2" sx={{ color: isDarkMode ? '#aaa' : '#555' }}>Filters</Typography>
                            </Box>
                            <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
                                <input placeholder="Search User" value={responseFilterUser} onChange={e => setResponseFilterUser(e.target.value)}
                                    style={{
                                        flex: 1, minWidth: '120px', padding: '8px 12px',
                                        backgroundColor: isDarkMode ? '#111' : '#fff',
                                        border: isDarkMode ? '1px solid #333' : '1px solid #ccc',
                                        borderRadius: '6px',
                                        color: isDarkMode ? '#fff' : '#000',
                                        fontSize: '0.8rem', outline: 'none'
                                    }} />
                                <input placeholder="Search task name" value={responseFilterTask} onChange={e => setResponseFilterTask(e.target.value)}
                                    style={{
                                        flex: 1, minWidth: '140px', padding: '8px 12px',
                                        backgroundColor: isDarkMode ? '#111' : '#fff',
                                        border: isDarkMode ? '1px solid #333' : '1px solid #ccc',
                                        borderRadius: '6px',
                                        color: isDarkMode ? '#fff' : '#000',
                                        fontSize: '0.8rem', outline: 'none'
                                    }} />
                                <Select size="small" displayEmpty value={responseFilterType} onChange={e => setResponseFilterType(e.target.value)} sx={{ ...selectSx, minWidth: 150 }}
                                    renderValue={(selected) => {
                                        if (selected === "") {
                                            return <span style={{ color: isDarkMode ? '#888' : '#999' }}>Select Task Type</span>;
                                        }
                                        if (selected === "all") {
                                            return "All";
                                        }
                                        return selected.charAt(0).toUpperCase() + selected.slice(1);
                                    }}
                                    MenuProps={{ PaperProps: { sx: { bgcolor: isDarkMode ? '#1e1e1e' : '#fff', color: isDarkMode ? '#fff' : '#000', border: isDarkMode ? '1px solid #333' : '1px solid #ccc', '& .MuiMenuItem-root': { '&:hover': { bgcolor: isDarkMode ? '#333' : '#f5f5f5' }, '&.Mui-selected': { bgcolor: '#4f5b93 !important', color: '#fff' } } } } }}>
                                    <MenuItem value="all">All</MenuItem>
                                    <MenuItem value="workout">Workout</MenuItem>
                                    <MenuItem value="food">Food</MenuItem>
                                    <MenuItem value="beverage">Beverage</MenuItem>
                                </Select>
                                <Select size="small" displayEmpty value={responseFilterStatus} onChange={e => setResponseFilterStatus(e.target.value)} sx={{ ...selectSx, minWidth: 130 }}
                                    renderValue={(selected) => {
                                        if (selected === "") {
                                            return <span style={{ color: isDarkMode ? '#888' : '#999' }}>Select Status</span>;
                                        }
                                        if (selected === "all") {
                                            return "All";
                                        }
                                        return selected.charAt(0).toUpperCase() + selected.slice(1);
                                    }}
                                    MenuProps={{ PaperProps: { sx: { bgcolor: isDarkMode ? '#1e1e1e' : '#fff', color: isDarkMode ? '#fff' : '#000', border: isDarkMode ? '1px solid #333' : '1px solid #ccc', '& .MuiMenuItem-root': { '&:hover': { bgcolor: isDarkMode ? '#333' : '#f5f5f5' }, '&.Mui-selected': { bgcolor: '#4f5b93 !important', color: '#fff' } } } } }}>
                                    <MenuItem value="all">All</MenuItem>
                                    <MenuItem value="pending">Pending</MenuItem>
                                    <MenuItem value="completed">Completed</MenuItem>
                                    <MenuItem value="partial">Partial</MenuItem>
                                    <MenuItem value="missed">Missed</MenuItem>
                                </Select>
                                <DateRangePicker
                                    fromDate={responseDateFrom}
                                    toDate={responseDateTo}
                                    onChange={(from, to) => {
                                        setResponseDateFrom(from);
                                        setResponseDateTo(to);
                                    }}
                                />
                                <Button startIcon={<ReplayIcon fontSize="small" />} onClick={handleResetResponseFilters}
                                    sx={{
                                        color: isDarkMode ? '#888' : '#666',
                                        textTransform: 'none',
                                        border: isDarkMode ? '1px solid #333' : '1px solid #ccc',
                                        borderRadius: '6px', px: 2,
                                        '&:hover': { color: isDarkMode ? '#fff' : '#000', borderColor: isDarkMode ? '#555' : '#888', backgroundColor: isDarkMode ? 'transparent' : 'rgba(0,0,0,0.04)' }
                                    }}>
                                    Reset
                                </Button>
                            </Box>
                        </Box>

                        <Box sx={{
                            backgroundColor: isDarkMode ? '#1a1a1a' : '#fff',
                            border: isDarkMode ? '1px solid #222' : '1px solid #e0e0e0',
                            borderRadius: '8px', p: 1.5,
                            boxShadow: isDarkMode ? '0 6px 20px rgba(0,0,0,0.25)' : '0 4px 12px rgba(0,0,0,0.05)',
                            position: 'relative', minHeight: '200px'
                        }}>
                            {filterLoading && (
                                <Box sx={{
                                    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                                    backgroundColor: isDarkMode ? 'rgba(26,26,26,0.7)' : 'rgba(255,255,255,0.7)',
                                    zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px'
                                }}>
                                    <Box sx={{
                                        width: 32, height: 32,
                                        border: isDarkMode ? '3px solid #333' : '3px solid #e0e0e0',
                                        borderTop: '3px solid #3c50b4', borderRadius: '50%',
                                        animation: 'spin 0.8s linear infinite', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } }
                                    }} />
                                </Box>
                            )}
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={headCellSx}>User</TableCell>
                                            <TableCell sx={headCellSx}>Task Name</TableCell>
                                            <TableCell sx={headCellSx}>Task Type</TableCell>
                                            <TableCell sx={headCellSx}>Assigned Date</TableCell>
                                            <TableCell sx={headCellSx}>Due Date</TableCell>
                                            <TableCell sx={headCellSx}>Response</TableCell>
                                            <TableCell sx={headCellSx}>Status</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredResponses.map((row) => (
                                            <TableRow key={row.id} onClick={() => { setSelectedResponse(row); setOpenResponseDrawer(true); }}
                                                sx={{ cursor: 'pointer', '&:hover': { backgroundColor: isDarkMode ? 'rgba(92,107,192,0.06)' : 'rgba(92,107,192,0.03)' } }}>
                                                <TableCell sx={cellSx}>
                                                    <Box display="flex" alignItems="center" gap={1.5}>
                                                        <Box sx={{ width: 30, height: 30, borderRadius: '50%', backgroundColor: isDarkMode ? '#333' : '#e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 'bold', color: isDarkMode ? '#fff' : '#333' }}>
                                                            {row.customer?.name?.charAt(0) || 'U'}
                                                        </Box>
                                                        <Box>
                                                            <Typography variant="body2" sx={{ color: isDarkMode ? '#fff' : '#000', fontSize: '0.8rem', fontWeight: 'bold' }}>{row.customer?.name}</Typography>
                                                            <Typography variant="caption" sx={{ color: isDarkMode ? '#666' : '#777', fontSize: '0.7rem' }}>{row.customer?.email}</Typography>
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={cellSx}>{row.dailyTask?.title}</TableCell>
                                                <TableCell sx={cellSx}>
                                                    <Chip label={row.dailyTask?.taskType} size="small" sx={{
                                                        fontSize: '0.65rem', borderRadius: '4px',
                                                        backgroundColor: row.dailyTask?.taskType === 'workout' ? (isDarkMode ? 'rgba(156,39,176,0.15)' : 'rgba(156,39,176,0.08)') : (isDarkMode ? 'rgba(255,152,0,0.15)' : 'rgba(239,108,0,0.08)'),
                                                        color: row.dailyTask?.taskType === 'workout' ? (isDarkMode ? '#ce93d8' : '#7b1fa2') : (isDarkMode ? '#ffb74d' : '#e65100'),
                                                    }} />
                                                </TableCell>
                                                <TableCell sx={cellSx}>{formatDate(row.createdAt || row.date)}</TableCell>
                                                <TableCell sx={cellSx}>{formatDate(row.date)}</TableCell>
                                                <TableCell sx={cellSx}>{row.value || '—'}</TableCell>
                                                <TableCell sx={cellSx}>
                                                    <Chip label={row.status} size="small" sx={{ fontSize: '0.65rem', borderRadius: '4px', backgroundColor: responseStatusColors[row.status]?.bg, color: responseStatusColors[row.status]?.color }} />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {!filterLoading && filteredResponses.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={7} sx={{ textAlign: 'center', py: 6, borderBottom: 'none' }}>
                                                    <Typography variant="body2" sx={{ color: isDarkMode ? '#666' : '#999', fontSize: '0.85rem' }}>No data found</Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                        <TablePagination
                            component="div"
                            count={responseRowCount}
                            page={responsePage}
                            onPageChange={(e, p) => setResponsePage(p)}
                            rowsPerPage={10}
                            rowsPerPageOptions={[10]}
                            sx={{
                                color: isDarkMode ? '#888' : '#555',
                                '.MuiTablePagination-selectIcon': { color: isDarkMode ? '#888' : '#555' },
                                '.MuiTablePagination-select': { color: isDarkMode ? '#fff' : '#000' }
                            }}
                        />
                    </Box>
                )}
            </Box>

            <TaskDialog
                open={openDialog}
                onClose={handleCloseDialog}
                isEditMode={isEditMode}
                taskDetails={taskDetails}
                setTaskDetails={setTaskDetails}
                handleSaveTask={handleSaveTask}
            />

            <AssignTaskDialog
                open={openAssignDialog}
                onClose={() => setOpenAssignDialog(false)}
                task={taskToAssign}
                onRefresh={fetchData}
            />

            <EditAssignmentDialog
                open={openEditAssignment}
                onClose={() => setOpenEditAssignment(false)}
                assignment={editAssignment}
                setAssignment={setEditAssignment}
                handleSave={handleSaveEditAssignment}
            />

            <ResponseDetailsDrawer
                open={openResponseDrawer}
                onClose={() => setOpenResponseDrawer(false)}
                response={selectedResponse}
            />

            <Dialog
                open={openDeleteConfirm}
                onClose={() => setOpenDeleteConfirm(false)}
                PaperProps={{
                    sx: {
                        backgroundColor: isDarkMode ? '#1a1a1a' : '#fff',
                        color: isDarkMode ? '#fff' : '#000',
                        borderRadius: '12px',
                        border: isDarkMode ? '1px solid #333' : '1px solid #e0e0e0'
                    }
                }}
            >
                <DialogTitle sx={{ fontWeight: 'bold' }}>Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: isDarkMode ? '#aaa' : '#555' }}>
                        Are you sure you want to delete this {deleteItemType}? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 2.5 }}>
                    <Button onClick={() => setOpenDeleteConfirm(false)} sx={{ color: isDarkMode ? '#888' : '#666', textTransform: 'none' }}>Cancel</Button>
                    <Button onClick={handleConfirmDelete} variant="contained" color="error" sx={{ textTransform: 'none', borderRadius: '8px' }}>Delete</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
